import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme
} from '@mui/material';

// Authentication imports
import { useAuth } from '../../hooks/auth/useAuth';

// Import individual analytics components
import RoadAnalytics from './RoadAnalytics';
import BridgeAnalytics from './BridgeAnalytics';
import SlopeAnalytics from './SlopeAnalytics';

const AnalyticsPage = () => {
  const { isAuthenticated, user, getUserOrganization } = useAuth();
  const { type } = useParams(); // Get analytics type from URL
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationName = userOrganization?.name || '';
  
  // Default to 'road' if no type specified or redirect from old /analytics route
  const analyticsType = type || 'road';
  
  // Redirect if accessing /analytics without type
  useEffect(() => {
    if (!type) {
      navigate('/analytics/road', { replace: true });
    }
  }, [type, navigate]);

  // Function to render content based on analytics type
  const renderAnalyticsContent = () => {
    switch (analyticsType) {
      case 'road':
        return <RoadAnalytics />;
      case 'bridge':
        return <BridgeAnalytics />;
      case 'slope':
        return <SlopeAnalytics />;
      default:
        return <RoadAnalytics />;
    }
  };

  return (
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Authentication Check */}
      {!isAuthenticated && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You must be logged in to access this page.
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {isAuthenticated && (
        <>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {analyticsType === 'road' && 'Road Analytics'}
              {analyticsType === 'bridge' && 'Bridge Analytics'}
              {analyticsType === 'slope' && 'Slope Analytics'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {analyticsType === 'road' && 'Data insights and performance analytics for road asset management'}
              {analyticsType === 'bridge' && 'Comprehensive analytics for bridge infrastructure monitoring and maintenance'}
              {analyticsType === 'slope' && 'Advanced analytics for slope stability and geotechnical assessment'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {organizationName && user?.role !== 'system_admin' && (
                <>
                  <span>{organizationName}</span>
                </>
              )}
              {user?.role === 'system_admin' && (
                <>
                  <span style={{ fontWeight: 500, color: theme.palette.warning.main }}>
                    System Admin View (All Organizations)
                  </span>
                </>
              )}
              {user?.permissionScope === 'network_section' && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 500, color: theme.palette.info.main }}>
                    Limited Access ({(user.accessible_networks?.length || 0)} networks, {(user.accessible_sections?.length || 0)} sections)
                  </span>
                </>
              )}
            </Typography>
          </Box>

          {/* Analytics Content */}
          {renderAnalyticsContent()}
        </>
      )}
    </Box>
  );
};

export default AnalyticsPage;